/**
 * Node Type Guards - Functions for type-safe node type checking
 */

import { TreeNode, isNodeOfType } from './TreeModel';
import {
  ConnectionTreeNode,
  DatabaseTreeNode,
  DatabasesFolderNode,
  SystemDatabasesFolderNode,
  SchemaContainerFolderNode,
  TablesFolderNode,
  ViewsFolderNode,
  ProgrammabilityFolderNode,
  StoredProceduresFolderNode,
  FunctionsFolderNode,
  ColumnsFolderNode,
  KeysFolderNode,
  ConstraintsFolderNode,
  TriggersFolderNode,
  IndexesFolderNode,
  StatisticsFolderNode,
  PartitionsFolderNode,
  TableSubfolderNode,
  DatabaseObjectTreeNode,
  ColumnTreeNode,
  MessageTreeNode,
} from './nodeTypes';

export function isConnectionNode(node: TreeNode): node is ConnectionTreeNode {
  return isNodeOfType<ConnectionTreeNode>(node, 'connection');
}

export function isDatabaseNode(node: TreeNode): node is DatabaseTreeNode {
  return isNodeOfType<DatabaseTreeNode>(node, 'database');
}

export function isDatabasesFolderNode(node: TreeNode): node is DatabasesFolderNode {
  return isNodeOfType<DatabasesFolderNode>(node, 'databases-folder');
}

export function isSystemDatabasesFolderNode(node: TreeNode): node is SystemDatabasesFolderNode {
  return isNodeOfType<SystemDatabasesFolderNode>(node, 'system-databases-folder');
}

export function isSchemaContainerFolderNode(node: TreeNode): node is SchemaContainerFolderNode {
  return isNodeOfType<SchemaContainerFolderNode>(node, 'schema-container-folder');
}

export function isTablesFolderNode(node: TreeNode): node is TablesFolderNode {
  return isNodeOfType<TablesFolderNode>(node, 'tables-folder');
}

export function isViewsFolderNode(node: TreeNode): node is ViewsFolderNode {
  return isNodeOfType<ViewsFolderNode>(node, 'views-folder');
}

export function isProgrammabilityFolderNode(node: TreeNode): node is ProgrammabilityFolderNode {
  return isNodeOfType<ProgrammabilityFolderNode>(node, 'programmability-folder');
}

export function isStoredProceduresFolderNode(node: TreeNode): node is StoredProceduresFolderNode {
  return isNodeOfType<StoredProceduresFolderNode>(node, 'stored-procedures-folder');
}

export function isFunctionsFolderNode(node: TreeNode): node is FunctionsFolderNode {
  return isNodeOfType<FunctionsFolderNode>(node, 'functions-folder');
}

export function isDatabaseObjectNode(node: TreeNode): node is DatabaseObjectTreeNode {
  return [
    'table', 'view', 'stored-procedure', 'function',
    'package', 'sequence', 'synonym', 'db-link', 'materialized-view', 'type',
    'directory',
  ].includes(node.type);
}

export function isColumnsFolderNode(node: TreeNode): node is ColumnsFolderNode {
  return isNodeOfType<ColumnsFolderNode>(node, 'columns-folder');
}

export function isKeysFolderNode(node: TreeNode): node is KeysFolderNode {
  return isNodeOfType<KeysFolderNode>(node, 'keys-folder');
}

export function isConstraintsFolderNode(node: TreeNode): node is ConstraintsFolderNode {
  return isNodeOfType<ConstraintsFolderNode>(node, 'constraints-folder');
}

export function isTriggersFolderNode(node: TreeNode): node is TriggersFolderNode {
  return isNodeOfType<TriggersFolderNode>(node, 'triggers-folder');
}

export function isIndexesFolderNode(node: TreeNode): node is IndexesFolderNode {
  return isNodeOfType<IndexesFolderNode>(node, 'indexes-folder');
}

export function isStatisticsFolderNode(node: TreeNode): node is StatisticsFolderNode {
  return isNodeOfType<StatisticsFolderNode>(node, 'statistics-folder');
}

export function isPartitionsFolderNode(node: TreeNode): node is PartitionsFolderNode {
  return isNodeOfType<PartitionsFolderNode>(node, 'partitions-folder');
}

export function isTableSubfolderNode(node: TreeNode): node is TableSubfolderNode {
  return [
    'columns-folder',
    'keys-folder',
    'constraints-folder',
    'triggers-folder',
    'indexes-folder',
    'statistics-folder',
    'partitions-folder'
  ].includes(node.type);
}

export function isColumnNode(node: TreeNode): node is ColumnTreeNode {
  return isNodeOfType<ColumnTreeNode>(node, 'column');
}

export function isMessageNode(node: TreeNode): node is MessageTreeNode {
  return ['message', 'error'].includes(node.type);
}

/**
 * Discriminator for any `pinned-*` wrapper row (the rows the user pins to the
 * top of a connection). Centralized so adding a new pinned kind only needs
 * one edit instead of touching every call site that checks for pinned rows.
 */
export const PINNED_WRAPPER_TYPES = [
  'pinned-database',
  'pinned-schema',
  'pinned-folder',
  'pinned-object',
  'pinned-workspace',
] as const;

export function isPinnedWrapperType(type: string): boolean {
  return (PINNED_WRAPPER_TYPES as readonly string[]).includes(type);
}
